import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CheckSquare,
  ChevronsUpDown,
  Cloud,
  CloudOff,
  Loader2,
  LogOut,
  Menu as MenuIcon,
  Moon,
  NotebookPen,
  Search,
  Settings,
  Sun,
  Sunrise,
  Target,
  X,
} from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { useSettings } from "@/hooks/useDomain";
import { useThemeEffect } from "@/hooks/useThemeEffect";
import { useThemeStore } from "@/lib/theme";
import { cn } from "@/lib/cn";
import { Menu } from "@/ui/Menu";
import { EventDialog } from "@/components/EventDialog";
import { ReminderDialog } from "@/components/ReminderDialog";
import { CommandPalette } from "@/components/CommandPalette";

const NAV = [
  { to: "/", label: "Hôm nay", icon: Sunrise, end: true },
  { to: "/calendar", label: "Lịch", icon: CalendarDays },
  { to: "/tasks", label: "Việc", icon: CheckSquare },
  { to: "/habits", label: "Thói quen", icon: Target },
  { to: "/notes", label: "Ghi chú", icon: NotebookPen },
];

export function Shell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  const settings = useSettings();
  const location = useLocation();
  const adoptTheme = useThemeStore((s) => s.adopt);

  useThemeEffect();

  // The theme is part of synced settings, so a choice made on one device
  // follows the account to the others.
  useEffect(() => {
    if (settings?.themeMode) adoptTheme(settings.themeMode);
  }, [settings?.themeMode, adoptTheme]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      if (event.key === "/" && !typing) {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (event.key === "n" && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        setEventOpen(true);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex h-full bg-paper">
      <aside className="hidden w-[236px] shrink-0 border-r border-line bg-shell lg:flex lg:flex-col">
        <SidebarBody onOpenSearch={() => setPaletteOpen(true)} />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 animate-fade-in bg-black/35"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <div className="relative flex h-full w-[272px] animate-slide-in-right flex-col border-r border-line bg-shell shadow-pop">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 top-3 rounded-md p-1.5 text-ink-faint hover:bg-paper hover:text-ink"
              aria-label="Đóng menu"
            >
              <X size={16} />
            </button>
            <SidebarBody onOpenSearch={() => setPaletteOpen(true)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <MobileHeader
          onOpenMenu={() => setDrawerOpen(true)}
          onOpenSearch={() => setPaletteOpen(true)}
        />

        <main className="min-w-0 flex-1 overflow-y-auto pb-[calc(4.25rem+env(safe-area-inset-bottom))] lg:pb-0">
          <Outlet />
        </main>

        <BottomNav />
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onCreateEvent={() => setEventOpen(true)}
        onCreateTask={() => setTaskOpen(true)}
      />
      <EventDialog open={eventOpen} onClose={() => setEventOpen(false)} />
      <ReminderDialog open={taskOpen} onClose={() => setTaskOpen(false)} />
    </div>
  );
}

function SidebarBody({ onOpenSearch }: { onOpenSearch: () => void }) {
  const user = useAppStore((s) => s.user);
  const syncing = useAppStore((s) => s.syncing);
  const lastSyncAt = useAppStore((s) => s.lastSyncAt);
  const syncError = useAppStore((s) => s.syncError);
  const sync = useAppStore((s) => s.sync);
  const logout = useAppStore((s) => s.logout);
  const settings = useSettings();
  const themeMode = useThemeStore((s) => s.mode);
  const setTheme = useThemeStore((s) => s.setMode);
  const navigate = useNavigate();

  function chooseTheme(mode: "system" | "light" | "dark") {
    setTheme(mode);
    const record = useAppStore
      .getState()
      .data.settings.find((item) => item.id === "singleton");
    if (record) void useAppStore.getState().upsert("settings", { ...record, themeMode: mode, updatedAt: Date.now() });
  }

  return (
    <>
      <div className="flex items-center gap-2 px-4 pb-3 pt-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-ink text-[13px] font-bold text-paper">
          L
        </div>
        <span className="text-[15px] font-semibold tracking-tight">LockCal</span>
      </div>

      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={onOpenSearch}
          className="flex w-full items-center gap-2 rounded-md border border-line bg-paper px-2.5 py-1.5 text-[12px] text-ink-faint transition hover:border-ink-faint hover:text-ink-soft"
        >
          <Search size={13} />
          <span className="flex-1 text-left">Tìm kiếm…</span>
          <kbd className="rounded border border-line px-1 py-0.5 font-sans text-[10px]">
            ⌘K
          </kbd>
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 px-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition",
                isActive
                  ? "bg-paper font-medium text-ink shadow-sm"
                  : "text-ink-soft hover:bg-paper/70 hover:text-ink",
              )
            }
          >
            <item.icon size={16} className="shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-line p-2.5">
        {/* Status only: syncing is automatic after every change and on focus. */}
        <div
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[11px] text-ink-faint"
          title="Đồng bộ tự động"
        >
          {syncing ? (
            <Loader2 size={13} className="animate-spin" />
          ) : syncError ? (
            <CloudOff size={13} className="text-danger" />
          ) : (
            <Cloud size={13} />
          )}
          <span className="flex-1 truncate text-left" aria-live="polite">
            {syncing
              ? "Đang đồng bộ…"
              : syncError
                ? "Lỗi đồng bộ"
                : lastSyncAt
                  ? `Đã sync ${formatRelative(lastSyncAt)}`
                  : "Chưa đồng bộ"}
          </span>
        </div>

        <div className="mt-1 flex items-center gap-1.5">
          <Menu
            side="top"
            align="start"
            className="flex-1"
            trigger={
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-paper"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-paper">
                  {(user?.displayName ?? user?.email ?? "?").slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-medium leading-4">
                    {user?.displayName ?? "Người dùng"}
                  </span>
                  <span className="block truncate text-[10px] leading-4 text-ink-faint">
                    {user?.email}
                  </span>
                </span>
                <ChevronsUpDown size={13} className="shrink-0 text-ink-faint" />
              </button>
            }
            items={[
              {
                label: "Cài đặt",
                icon: Settings,
                onSelect: () => navigate("/settings"),
              },
              {
                label:
                  themeMode === "dark" ? "Giao diện sáng" : "Giao diện tối",
                icon: themeMode === "dark" ? Sun : Moon,
                onSelect: () =>
                  chooseTheme(themeMode === "dark" ? "light" : "dark"),
              },
              {
                label: "Theo hệ thống",
                icon: Settings,
                onSelect: () => chooseTheme("system"),
              },
              {
                label: "Đăng xuất",
                icon: LogOut,
                danger: true,
                separatorAbove: true,
                onSelect: () => void logout(),
              },
            ]}
          />
          <button
            type="button"
            onClick={() => chooseTheme(themeMode === "dark" ? "light" : "dark")}
            className="rounded-md p-2 text-ink-faint transition hover:bg-paper hover:text-ink"
            aria-label={
              themeMode === "dark"
                ? "Chuyển sang giao diện sáng"
                : "Chuyển sang giao diện tối"
            }
            title={
              themeMode === "dark" ? "Giao diện sáng" : "Giao diện tối"
            }
          >
            {themeMode === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </div>
      </div>
    </>
  );
}

function MobileHeader({
  onOpenMenu,
  onOpenSearch,
}: {
  onOpenMenu: () => void;
  onOpenSearch: () => void;
}) {
  const syncing = useAppStore((s) => s.syncing);
  const syncError = useAppStore((s) => s.syncError);

  return (
    <header className="sticky top-0 z-20 flex h-12 shrink-0 items-center gap-1 border-b border-line bg-shell/90 px-2 backdrop-blur lg:hidden">
      <button
        type="button"
        onClick={onOpenMenu}
        className="rounded-md p-2 text-ink-soft transition hover:bg-paper"
        aria-label="Mở menu"
      >
        <MenuIcon size={18} />
      </button>
      <span className="flex-1 text-[14px] font-semibold tracking-tight">
        LockCal
      </span>
      <span aria-live="polite" className="px-1">
        {syncing ? (
          <Loader2 size={14} className="animate-spin text-ink-faint" />
        ) : syncError ? (
          <CloudOff size={14} className="text-danger" />
        ) : (
          <Cloud size={14} className="text-ink-faint" />
        )}
      </span>
      <button
        type="button"
        onClick={onOpenSearch}
        className="rounded-md p-2 text-ink-soft transition hover:bg-paper"
        aria-label="Tìm kiếm"
      >
        <Search size={17} />
      </button>
    </header>
  );
}

function BottomNav() {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-paper/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
      aria-label="Điều hướng chính"
    >
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              "flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition",
              isActive ? "text-ink" : "text-ink-faint",
            )
          }
        >
          <item.icon size={19} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "vừa xong";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} phút trước`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} giờ trước`;
  return "hôm nay";
}
